// ============================================================================
// WIDGET SANDBOX — Host Component
// Renders AI-generated widgets in a sandboxed iframe with postMessage bridge
// ============================================================================

import { useRef, useEffect, useState, useCallback, type CSSProperties } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import { BeoflowButton } from '@/components/BeoflowButton'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { beoflowColors } from '@/theme/muiTheme'
import { useAuth } from '@/contexts/AuthContext'
import { useAthleteOptional } from '@/contexts/AthleteContext'
import { useThemeMode } from '@/contexts/ThemeContext'
import { extractWidgetThemeTokens } from './theme-tokens'
import { useWidgetDataBridge } from './useWidgetDataBridge'
import type { BridgeUserInfo } from './useWidgetDataBridge'
import type {
  HostToSandboxMessage,
  SandboxToHostMessage,
  RenderWidgetMessage,
  WidgetContext,
} from './types'

interface WidgetSandboxProps {
  code: string
  widgetId?: string
  context?: 'view' | 'chat'
  // The date range the host is currently showing (e.g. the calendar's visible
  // week) — forwarded to the sandbox so widget code can scope its own data
  // fetches instead of assuming "today". Distinct from `context` above, which
  // is the render-environment mode (view vs chat), not a date range.
  dateContext?: WidgetContext
  onError?: (error: string) => void
  onFix?: (error: string) => void
  onSave?: () => void
  sx?: CSSProperties | Record<string, unknown>
}

type SandboxState = 'loading' | 'ready' | 'rendered' | 'error'

function getSandboxUrl(): string {
  return `${import.meta.env.BASE_URL}sandbox-index.html`
}

export function WidgetSandbox({
  code,
  widgetId,
  context = 'view',
  dateContext,
  onError,
  onFix,
  sx,
}: WidgetSandboxProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [state, setState] = useState<SandboxState>('loading')
  const [error, setError] = useState<string | null>(null)
  const [contentHeight, setContentHeight] = useState<number>(300)
  const codeRef = useRef(code)
  codeRef.current = code
  const dateContextRef = useRef(dateContext)
  dateContextRef.current = dateContext

  const { mode } = useThemeMode()

  // Derive user info from AuthContext for the data bridge
  const { user } = useAuth()
  // Optional: tolerate rendering outside an AthleteProvider (viewer's own data).
  const selectedProfileId = useAthleteOptional()?.selectedProfileId ?? null
  const bridgeUser: BridgeUserInfo | undefined = user
    ? { id: user.email, name: `${user.first_name} ${user.last_name}`.trim() }
    : undefined

  // Wire up the data bridge (with athlete profile pass-through)
  useWidgetDataBridge(iframeRef, state === 'ready' || state === 'rendered', bridgeUser, selectedProfileId)

  const sendToSandbox = useCallback((msg: HostToSandboxMessage) => {
    iframeRef.current?.contentWindow?.postMessage(msg, '*')
  }, [])

  const sendRender = useCallback(() => {
    const tokens = extractWidgetThemeTokens(mode)
    const msg: RenderWidgetMessage = {
      type: 'RENDER_WIDGET',
      code: codeRef.current,
      theme: tokens,
      context: dateContextRef.current,
    }
    sendToSandbox(msg)
  }, [sendToSandbox, mode])

  // Listen for messages from sandbox
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return

      const msg = event.data as SandboxToHostMessage
      switch (msg?.type) {
        case 'SANDBOX_READY':
          setState('ready')
          // Send current container dimensions before render — the ResizeObserver
          // likely fired before the iframe was ready, so that message was lost.
          if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect()
            if (rect.width > 0 && rect.height > 0) {
              sendToSandbox({ type: 'CONTAINER_RESIZE', width: Math.round(rect.width), height: Math.round(rect.height) })
            }
          }
          sendRender()
          break
        case 'RENDER_SUCCESS':
          setState('rendered')
          setError(null)
          break
        case 'RENDER_ERROR':
          setState('error')
          setError(msg.error)
          console.error('[WidgetSandbox] Widget render failed', { widgetId, error: msg.error, stack: msg.stack })
          onError?.(msg.error)
          break
        case 'RESIZE':
          if (context === 'chat' && msg.height > 0) {
            setContentHeight(msg.height)
          }
          break
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [context, onError, sendRender, widgetId])

  // Re-send code when it changes, theme mode toggles, or the host's visible
  // date range moves (e.g. calendar week navigation).
  useEffect(() => {
    if (state === 'ready' || state === 'rendered' || state === 'error') {
      setState('ready')
      sendRender()
    }
    // Only re-render when code, mode, the date range, or the selected athlete
    // changes, not on state changes. selectedProfileId belongs here because the
    // bridge injects target_profile_id at REQUEST time — without a re-render the
    // widget keeps the previous athlete's data (BEO-3095).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, mode, dateContext?.viewStartDate, dateContext?.viewEndDate, dateContext?.activityPlanId, selectedProfileId])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      sendToSandbox({ type: 'DESTROY' })
    }
  }, [sendToSandbox])

  // Observe container size and forward to sandbox so widgets can use containerWidth/containerHeight
  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) return
      const { width, height } = entry.contentRect
      if (width > 0 && height > 0) {
        sendToSandbox({ type: 'CONTAINER_RESIZE', width: Math.round(width), height: Math.round(height) })
      }
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [sendToSandbox])

  const iframeHeight = context === 'chat' ? contentHeight : '100%'

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        width: '100%',
        height: context === 'chat' ? 'auto' : '100%',
        minHeight: context === 'chat' ? 100 : 200,
        overflow: 'hidden',
        borderRadius: '12px',
        // Card spec v2: the widget tile IS the card — fill-only surface, no border.
        backgroundColor: `${beoflowColors.beoWhite}0D`,
        ...sx,
      }}
    >
      {/* Sandboxed iframe */}
      <iframe
        ref={iframeRef}
        src={getSandboxUrl()}
        // Never add allow-top-navigation: Beoflow Desktop treats a top navigation to
        // /desktop-signin as a sign-in request (beoflow-desktop main.rs on_navigation).
        sandbox="allow-scripts"
        title={widgetId ? `Widget: ${widgetId}` : 'Beoflow Widget'}
        style={{
          width: '100%',
          height: iframeHeight,
          border: 'none',
          display: state === 'error' ? 'none' : 'block',
          background: 'transparent',
          colorScheme: mode,
        }}
      />

      {/* Loading overlay */}
      {state === 'loading' && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '12px',
            backgroundColor: `${beoflowColors.beoBlack}CC`,
          }}
        >
          <CircularProgress
            size={32}
            sx={{ color: beoflowColors.meanBlue }}
          />
        </Box>
      )}

      {/* Error state */}
      {state === 'error' && error && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            p: 3,
            backgroundColor: beoflowColors.graphGreyDark,
            border: `1px solid ${beoflowColors.redBase}80`,
            borderRadius: '12px',
          }}
        >
          <ErrorOutlineIcon
            sx={{ fontSize: 40, color: beoflowColors.redBase }}
          />
          <Typography
            sx={{
              color: beoflowColors.beoWhite,
              fontFamily: '"IBM Plex Mono", monospace',
              fontSize: '13px',
              textAlign: 'center',
              maxWidth: 400,
            }}
          >
            An error occurred with this widget.
          </Typography>
          {onFix && (
            <BeoflowButton
              onClick={() => onFix(error)}
              variant="outline"
              size="small"
              sx={{
                color: beoflowColors.beoWhite,
                borderColor: `${beoflowColors.beoWhite}40`,
                '&:hover': {
                  borderColor: beoflowColors.beoWhite,
                },
              }}
            >
              Fix
            </BeoflowButton>
          )}
        </Box>
      )}
    </Box>
  )
}
